import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Trash2, Eye, Printer, Link as LinkIcon, FolderPlus, Clock,
  CheckCircle2, Copy, X, PenLine, Search, Filter, Send,
} from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "./api";
import { buildFormHtml, printFormPdf } from "./formPdf";
import { LenderPackModal } from "./LenderPackModal";

const fmtDDMMYYYY = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

export const StatusChip = ({ status }) => {
  const map = {
    in_progress: { label: "In Progress", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: Clock },
    signed: { label: "Signed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  };
  const { label, cls, Icon } = map[status] || map.in_progress;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10.5px] font-bold tracking-[0.14em] uppercase px-2.5 py-1 border rounded ${cls}`}>
      <Icon size={11} strokeWidth={1.8} /> {label}
    </span>
  );
};

const AttachModal = ({ form, customers, onClose, onAttached }) => {
  const [cid, setCid] = useState(form.customer_id || "");
  const [working, setWorking] = useState(false);

  const attach = async () => {
    if (!cid) return;
    setWorking(true);
    try {
      const fd = new FormData();
      fd.append("customer_id", cid);
      // No pdf_html — the backend builds the PDF itself (ReportLab) so this
      // works on the live site. Sending a ~1 MB HTML blob used to time out
      // via Cloudflare and was the root cause of the "invalid or incomplete
      // response" error.
      await axios.post(`${API}/admin/forms/${form.id}/attach`, fd, authHeaders());
      toast.success("Filed under customer documents");
      onAttached();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not file");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-forest/40 backdrop-blur-sm flex items-center justify-center p-5">
      <div className="bg-white border border-forest/15 rounded-xl max-w-md w-full p-6 space-y-4" data-testid="forms-attach-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-lg text-forest">File to a customer</h3>
          <button type="button" onClick={onClose} className="p-1 text-forest/60 hover:text-forest"><X size={16} /></button>
        </div>
        <p className="text-[13px] text-sage">The signed application will appear under the chosen customer's Documents, filed as "Mortgage Application".</p>
        <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid="forms-attach-select"
          className="w-full bg-cream-3 border border-forest/15 px-3 py-2.5 text-sm focus:border-gold focus:outline-none">
          <option value="">Select a customer…</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</option>
          ))}
        </select>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="btn-ghost-dark">Cancel</button>
          <button type="button" onClick={attach} disabled={!cid || working} data-testid="forms-attach-confirm" className="btn-gold disabled:opacity-50">
            {working ? "Filing…" : "File"}
          </button>
        </div>
      </div>
    </div>
  );
};

const LinkModal = ({ form, onClose }) => {
  const url = `${window.location.origin}/apply/${form.token}`;
  const copy = () => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };
  return (
    <div className="fixed inset-0 z-50 bg-forest/40 backdrop-blur-sm flex items-center justify-center p-5">
      <div className="bg-white border border-forest/15 rounded-xl max-w-lg w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-lg text-forest">Share link</h3>
          <button type="button" onClick={onClose} className="p-1 text-forest/60 hover:text-forest"><X size={16} /></button>
        </div>
        <p className="text-[13px] text-sage">Send this private link to the customer. They can save progress and sign on any device.</p>
        <div className="flex gap-2">
          <input readOnly value={url} data-testid="forms-link-url"
            className="flex-1 bg-cream-3 border border-forest/15 px-3 py-2.5 text-[13px] font-mono focus:border-gold focus:outline-none" />
          <button type="button" onClick={copy} data-testid="forms-link-copy" className="btn-forest !py-2.5 inline-flex items-center gap-1.5"><Copy size={14} /> Copy</button>
        </div>
      </div>
    </div>
  );
};

/**
 * ApplicationsList — shared list used by the Customers master "Applications" tab
 * and by each customer's own "Applications" tab.
 *
 * Props:
 *   filterCustomerId — when set, only forms for that customer are shown.
 *   customers — list of customers for the attach dropdown. Optional when filterCustomerId is set.
 *   emptyMessage — override empty state copy.
 *   reloadKey — bump to force a reload.
 */
export const ApplicationsList = ({ filterCustomerId = null, customers = [], emptyMessage, reloadKey = 0 }) => {
  const [list, setList] = useState(null);
  const [attachFor, setAttachFor] = useState(null);
  const [lenderPackFor, setLenderPackFor] = useState(null);
  const [showLinkFor, setShowLinkFor] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [allCustomers, setAllCustomers] = useState(customers);

  const load = async () => {
    try {
      setLoadError("");
      const url = filterCustomerId
        ? `${API}/admin/customers/${filterCustomerId}/forms`
        : `${API}/admin/forms`;
      const { data } = await axios.get(url, authHeaders());
      setList(data || []);
      if (!filterCustomerId && customers.length === 0) {
        const { data: cs } = await axios.get(`${API}/admin/customers`, authHeaders());
        setAllCustomers(cs || []);
      } else {
        setAllCustomers(customers);
      }
    } catch (e) {
      setLoadError(formatApiErrorDetail(e.response?.data?.detail) || "The applications list didn't load.");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCustomerId, reloadKey]);

  const remove = async (f) => {
    if (!window.confirm("Delete this application and its draft/signed data?")) return;
    try {
      await axios.delete(`${API}/admin/forms/${f.id}`, authHeaders());
      await load();
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete");
    }
  };

  const openPrint = async (f) => {
    try {
      const { data } = await axios.get(`${API}/admin/forms/${f.id}`, authHeaders());
      printFormPdf(data);
    } catch { toast.error("Could not open form"); }
  };

  const openView = async (f) => {
    try {
      const { data } = await axios.get(`${API}/admin/forms/${f.id}`, authHeaders());
      const html = buildFormHtml(data);
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { toast.error("Could not open form"); }
  };

  const openEditor = (f) => {
    // The admin fills the application themselves in a new tab, using the same
    // private form page the client would see. The URL hash tells the form page
    // to hide the "share link" prompt and show an "I'm filling this on behalf
    // of the client" badge.
    window.open(`/apply/${f.token}#admin`, "_blank", "noopener");
  };

  const sorted = useMemo(
    () => (list || []).slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [list]
  );

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sorted.filter((f) => {
      if (statusFilter !== "all" && f.status !== statusFilter) return false;
      if (!q) return true;
      const hay = `${f.customer_name || ""} ${f.type || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [sorted, query, statusFilter]);

  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-5 py-4 rounded">
        <p>{loadError}</p>
        <button type="button" onClick={load} className="mt-2 underline">Try again</button>
      </div>
    );
  }
  if (list === null) return <p className="text-sm text-sage">Loading…</p>;
  if (list.length === 0) {
    return (
      <p className="text-sm text-sage bg-white border border-forest/10 rounded px-6 py-10 text-center">
        {emptyMessage || "No applications yet."}
      </p>
    );
  }

  const showCustomerCol = !filterCustomerId;

  return (
    <>
      {/* Search + status filter */}
      <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="applications-filter-bar">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} strokeWidth={1.8} className="absolute left-3 top-1/2 -translate-y-1/2 text-forest/40" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="applications-search-input"
            placeholder={showCustomerCol ? "Search by customer or type…" : "Search by type…"}
            className="w-full bg-white border border-forest/15 rounded pl-9 pr-3 py-2 text-[13px] focus:border-gold focus:outline-none"
          />
        </div>
        <span className="inline-flex items-center gap-1.5 text-[10.5px] font-bold tracking-[0.18em] uppercase text-forest/60"><Filter size={12} strokeWidth={1.8} /> Status</span>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="applications-status-filter"
          className="bg-white border border-forest/15 rounded px-3 py-1.5 text-[12px] focus:border-gold focus:outline-none"
        >
          <option value="all">{`All (${sorted.length})`}</option>
          <option value="in_progress">In Progress</option>
          <option value="signed">Signed</option>
        </select>
        {filtered.length !== sorted.length && (
          <span className="text-[11px] text-sage">Showing {filtered.length} of {sorted.length}</span>
        )}
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-sage bg-white border border-forest/10 rounded px-6 py-10 text-center" data-testid="applications-empty-filtered">
          No applications match your search.
        </p>
      ) : (
      <>
      <div className="hidden md:block bg-white border border-forest/10 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-forest/10 text-[10.5px] tracking-[0.18em] uppercase text-forest/60 bg-cream-3/40">
              {showCustomerCol && <th className="text-left px-5 py-3 font-bold">Customer</th>}
              <th className="text-left px-4 py-3 font-bold">Type</th>
              <th className="text-left px-4 py-3 font-bold">Date</th>
              <th className="text-left px-4 py-3 font-bold">Status</th>
              <th className="w-60 px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((f, i) => (
              <tr key={f.id} data-testid={`forms-row-${i + 1}`} className="border-b border-forest/5 last:border-b-0 hover:bg-cream-3/50">
                {showCustomerCol && <td className="px-5 py-4 font-semibold text-forest">{f.customer_name || <span className="text-sage italic">Unnamed</span>}</td>}
                <td className="px-4 py-4 text-sage capitalize">{f.type}</td>
                <td className="px-4 py-4 text-sage">{fmtDDMMYYYY(f.signed_at || f.created_at)}</td>
                <td className="px-4 py-4"><StatusChip status={f.status} /></td>
                <td className="px-4 py-4">
                  <div className="flex justify-end gap-1 flex-wrap">
                    {f.status !== "signed" && (
                      <button type="button" onClick={() => openEditor(f)} data-testid={`forms-fill-${i + 1}`} className="p-2.5 text-forest/70 hover:text-forest" title="Fill in / continue">
                        <PenLine size={16} strokeWidth={1.6} />
                      </button>
                    )}
                    <button type="button" onClick={() => setShowLinkFor(f)} data-testid={`forms-link-${i + 1}`} className="p-2.5 text-forest/70 hover:text-forest" title="Share link">
                      <LinkIcon size={16} strokeWidth={1.6} />
                    </button>
                    <button type="button" onClick={() => openView(f)} data-testid={`forms-view-${i + 1}`} className="p-2.5 text-forest/70 hover:text-forest" title="View">
                      <Eye size={16} strokeWidth={1.6} />
                    </button>
                    <button type="button" onClick={() => openPrint(f)} data-testid={`forms-print-${i + 1}`} className="p-2.5 text-forest/70 hover:text-forest" title="Print / Download PDF">
                      <Printer size={16} strokeWidth={1.6} />
                    </button>
                    {f.status === "signed" && (
                      <button type="button" onClick={() => setAttachFor(f)} data-testid={`forms-attach-${i + 1}`} className="p-2.5 text-forest hover:text-gold-dark" title="File to customer documents">
                        <FolderPlus size={16} strokeWidth={1.6} />
                      </button>
                    )}
                    {f.status === "signed" && f.customer_id && (
                      <button type="button" onClick={() => setLenderPackFor(f)} data-testid={`forms-lender-pack-${i + 1}`} className="p-2.5 text-forest hover:text-gold-dark" title="Build lender submission pack">
                        <Send size={16} strokeWidth={1.6} />
                      </button>
                    )}
                    <button type="button" onClick={() => remove(f)} data-testid={`forms-delete-${i + 1}`} className="p-2.5 text-forest/60 hover:text-red-600" title="Delete">
                      <Trash2 size={16} strokeWidth={1.6} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden space-y-3">
        {filtered.map((f, i) => (
          <div key={f.id} data-testid={`forms-card-${i + 1}`} className="bg-white border border-forest/10 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                {showCustomerCol && <p className="font-semibold text-forest">{f.customer_name || "Unnamed"}</p>}
                <p className="text-[12px] text-sage mt-0.5 capitalize">{f.type}</p>
                <p className="text-[11px] text-sage mt-1">{fmtDDMMYYYY(f.signed_at || f.created_at)}</p>
                <div className="mt-2"><StatusChip status={f.status} /></div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-forest/8 justify-end">
              {f.status !== "signed" && <button type="button" onClick={() => openEditor(f)} className="p-2 text-forest/70 hover:text-forest" title="Fill"><PenLine size={16} strokeWidth={1.6} /></button>}
              <button type="button" onClick={() => setShowLinkFor(f)} className="p-2 text-forest/70 hover:text-forest" title="Link"><LinkIcon size={16} strokeWidth={1.6} /></button>
              <button type="button" onClick={() => openView(f)} className="p-2 text-forest/70 hover:text-forest" title="View"><Eye size={16} strokeWidth={1.6} /></button>
              <button type="button" onClick={() => openPrint(f)} className="p-2 text-forest/70 hover:text-forest" title="Print"><Printer size={16} strokeWidth={1.6} /></button>
              {f.status === "signed" && <button type="button" onClick={() => setAttachFor(f)} className="p-2 text-forest hover:text-gold-dark" title="File"><FolderPlus size={16} strokeWidth={1.6} /></button>}
              {f.status === "signed" && f.customer_id && <button type="button" onClick={() => setLenderPackFor(f)} className="p-2 text-forest hover:text-gold-dark" title="Lender Pack"><Send size={16} strokeWidth={1.6} /></button>}
              <button type="button" onClick={() => remove(f)} className="p-2 text-forest/60 hover:text-red-600" title="Delete"><Trash2 size={16} strokeWidth={1.6} /></button>
            </div>
          </div>
        ))}
      </div>
      </>
      )}

      {showLinkFor && <LinkModal form={showLinkFor} onClose={() => setShowLinkFor(null)} />}
      {attachFor && <AttachModal form={attachFor} customers={allCustomers} onClose={() => setAttachFor(null)} onAttached={load} />}
      {lenderPackFor && <LenderPackModal form={lenderPackFor} onClose={() => setLenderPackFor(null)} onBuilt={load} />}
    </>
  );
};
